import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Save, Eye, Edit, ChevronLeft, ChevronRight } from 'lucide-react';

const ClientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [devis, setDevis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Reset states when ID changes
    setClient(null);
    setDevis(null);
    setLoading(true);

    const loadAllData = async () => {
      try {
        const clientPromise = axios.get(`${API}/clients/${id}`);
        const devisPromise = axios.get(`${API}/clients/${id}/devis`);
        const [clientRes, devisRes] = await Promise.all([clientPromise, devisPromise]);
        
        setClient(clientRes.data);
        setDevis(devisRes.data);

      } catch (error) {
        toast.error("Erreur lors du chargement des données du client.");
        navigate('/backoffice/clients');
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [id]);

  const loadDevis = async (url) => {
    if (!url) return;
    try {
      const res = await axios.get(url);
      setDevis(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des devis');
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClient({ ...client, [name]: value });
  };
  
  const handleTypeChange = (value) => {
    setClient({ ...client, type: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/clients/${id}`, client);
      toast.success('Client mis à jour avec succès');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du client');
    }
  };

  if (loading || !client) {
    return <div>Chargement...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" onClick={() => navigate('/backoffice/clients')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour à la liste
        </Button>
        <h1 className="text-2xl font-bold text-stone-800">
          Fiche Client: {client.prenom} {client.nom}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Détails du Client</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nom</Label>
                    <Input name="nom" value={client.nom} onChange={handleInputChange} />
                  </div>
                  <div>
                    <Label>Prénom</Label>
                    <Input name="prenom" value={client.prenom} onChange={handleInputChange} />
                  </div>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input name="email" type="email" value={client.email} onChange={handleInputChange} />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input name="telephone" value={client.telephone} onChange={handleInputChange} />
                </div>
                <div>
                  <Label>Adresse</Label>
                  <Input name="adresse" value={client.adresse || ''} onChange={handleInputChange} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label>Code Postal</Label>
                        <Input name="code_postal" value={client.code_postal || ''} onChange={handleInputChange} />
                    </div>
                    <div>
                        <Label>Ville</Label>
                        <Input name="ville" value={client.ville || ''} onChange={handleInputChange} />
                    </div>
                </div>
                <div>
                    <Label>Type</Label>
                    <Select value={client.type} onValueChange={handleTypeChange}>
                        <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un type" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="particulier">Particulier</SelectItem>
                            <SelectItem value="partenaire">Partenaire</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700">
                  <Save className="h-4 w-4 mr-2" />
                  Enregistrer les modifications
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Devis Associés</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Numéro</TableHead>
                    <TableHead>Événement</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devis && devis.data.length > 0 ? (
                    devis.data.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell>{d.numero}</TableCell>
                        <TableCell>{d.event_info?.event_type}</TableCell>
                        <TableCell>{new Date(d.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>{d.total_ttc}€</TableCell>
                        <TableCell>{d.statut}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/backoffice/devis/${d.id}/edit`}><Edit className="h-4 w-4" /></Link>
                          </Button>
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/devis/${d.id}`} target="_blank"><Eye className="h-4 w-4" /></Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan="6" className="text-center">Aucun devis trouvé.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {devis && devis.data.length > 0 && (
                <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-stone-500">
                      Page {devis.current_page} sur {devis.last_page}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadDevis(devis.prev_page_url)}
                        disabled={!devis.prev_page_url}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Précédent
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadDevis(devis.next_page_url)}
                        disabled={!devis.next_page_url}
                      >
                        Suivant
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClientDetails;
